import React, { ChangeEvent, useState, MouseEvent } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, TextField, Button } from '@mui/material';

const EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

interface FeedbackProps {
  show: boolean; // if the feedback should be shown
  handleClose: (e: MouseEvent, reason: 'close' | 'submit' | 'backdropClick' | 'escapeKeyDown') => void;
  feedbackFields: FeedbackField[]; // The feedback fields to display
}

interface FeedbackField {
  key: string;
  label: string;
}

function FeedBackSentMessage({ show, handleClose, feedbackFields }: FeedbackProps) {
  /**
   * Message to the user that the feedback was sent
   */
  return show ? (
    <>
      <DialogTitle>Thank you for your feedback!</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your feedback has been sent, we will review it as soon as we can. In the meantime, join our free
          <span style={{ display: 'inline' }}>
            {' '}
            <a
              href="https://join.slack.com/t/gritsoftwaresystemshq/shared_invite/zt-1kvxj8vao-wVuVE1oUyhcez2EY~UcZrA"
              target="_blank"
              rel="noreferrer noopener"
            >
              slack channel
            </a>{' '}
          </span>
          if have any further questions.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => handleClose(e, 'close')}>Close</Button>
      </DialogActions>
    </>
  ) : (
    <></>
  );
}

function FeedbackEdit({ show, handleClose, feedbackFields }: FeedbackProps) {
  /**
   * Component defining feedback text field and state
   */
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmailId') || '');
  const [userFeedback, setUserFeedback] = useState(
    Object.assign(
      {},
      ...feedbackFields.map((ff) => {
        return { [ff.key]: '' };
      }),
    ),
  );

  const makeControllingFunc = (setState: Function) => {
    /**
     * Creates a function that will call the function passed with the element's value
     * when the value changes
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
      setState(e.currentTarget.value);
    };
    return handleChange;
  };

  const submitFeedback = (e: MouseEvent) => {
    handleClose(e, 'submit');
    setUserFeedback(
      Object.assign(
        {},
        feedbackFields.map((ff) => {
          return { [ff.key]: '' };
        }),
      ),
    );
    console.log("Start wizard feedback submitted");
    console.log(userEmail);
    console.log(userFeedback);
  };

  const disableSubmitButton = () => {
    /**
     * Function that determines if submit button should be disbaled or not 
     */
    return !EMAIL_REGEX.test(userEmail);
  }

  return show ? (
    <>
      <DialogTitle>Send Feedback</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please fill out this short form with your feedback. We look forward to hearing from you!
        </DialogContentText>
        <TextField
          label="Email"
          sx={{ my: 2, mx: 8, width: '80%' }}
          value={userEmail}
          onChange={makeControllingFunc(setUserEmail)}
        />
        {feedbackFields.map((ff: FeedbackField, idx: number) => {
          return (
            <TextField
              key={idx}
              label={ff.label}
              sx={{ mx: 8, my: 1, width: '80%' }}
              multiline
              fullWidth
              minRows="6"
              maxRows="9"
              value={userFeedback[ff.key]}
              onChange={(e) => {
                setUserFeedback({ ...userFeedback, [ff.key]: e.target.value });
              }}
            />
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => handleClose(e, 'close')}>Close</Button>
        <Button 
          disabled={disableSubmitButton()}
          onClick={(e) => submitFeedback(e)}>Submit</Button>
      </DialogActions>
    </>
  ) : (
    <></>
  );
}

export function FeedBackPopup({ show, handleClose, feedbackFields }: FeedbackProps) {
  /**
   * Popup for the user to send feedback
   */
  const [showSubmitMessage, setShowMessage] = useState(false);
  const handleFormClose = (e: MouseEvent, code: 'submit' | 'close' | 'backdropClick' | 'escapeKeyDown') => {
    console.log(code);
    if (code === 'close') {
      handleClose(e, 'escapeKeyDown');
    } else if (code === 'submit') {
      setShowMessage(true);
    } else {
      handleClose(e, code);
    }
  };

  const handleFullDialogClose = (e: MouseEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
    handleClose(e, reason);
  };

  return (
    <Dialog open={show} onClose={handleFullDialogClose}>
      <FeedBackSentMessage show={showSubmitMessage} handleClose={handleClose} feedbackFields={[]} />
      <FeedbackEdit show={!showSubmitMessage} handleClose={handleFormClose} feedbackFields={feedbackFields} />
    </Dialog>
  );
}

export function useFeedbackButton(fields: FeedbackField[]) {
  /**
   * Creates and returns a feedback button, and a feedback popup
   * automaticall links them with a default behavior--only closing when the
   * close button is pressed, and returns both components and a flag feedbackShown
   */
  const [show, setShow] = useState(false);
  const onClose = (e: MouseEvent, reason: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    setShow(false);
  };
  return {
    feedbackShown: show,
    Feedback: () => <FeedBackPopup show={show} handleClose={onClose} feedbackFields={fields} />,
    FeedbackButton: () => <Button onClick={() => setShow(true)}>Send Feedback</Button>,
  };
}
